import { FC, useCallback } from 'react';
import {
  ObjectType,
  Spinner,
  useObjectLockQuery,
  useAccessGroupQuery,
  checkLockOnPage,
  useIsAdmin,
} from '@atlas-frontend/sdk';
import { path } from '../../paths';
import { useNavigateTo } from '../../paths/useNavigateTo';
import { useNumericParam } from '../../common/useEntityIdParam';
import { Forbidden } from '../Forbidden';
import { NotFound } from '../NotFound';
import { AccessGroupForm } from './AccessGroupForm';

export const AccessGroupEdit = () => {
  const accessGroupId = useNumericParam('id');

  if (!useIsAdmin()) {
    return <Forbidden />;
  }

  if (!accessGroupId) {
    return <NotFound />;
  }

  return <AccessGroupEditInner accessGroupId={accessGroupId} />;
};

const AccessGroupEditInner: FC<{ accessGroupId: number }> = ({
  accessGroupId,
}) => {
  const {
    data: accessGroup,
    isLoading,
    refetch,
  } = useAccessGroupQuery({
    id: accessGroupId,
  });
  const { data: lockData } = useObjectLockQuery({
    objectId: accessGroupId,
    objectType: ObjectType.AccessGroup,
  });
  const navigateToAccessGroupView = useNavigateTo(path.accessGroupDetails, {
    id: accessGroupId,
  });

  checkLockOnPage(lockData, () => navigateToAccessGroupView());

  const handleCancel = useCallback(() => {
    navigateToAccessGroupView();
  }, [navigateToAccessGroupView]);
  const handleSuccess = useCallback(() => {
    void refetch();
  }, [refetch]);

  if (isLoading) {
    return <Spinner />;
  }

  if (!accessGroup) {
    return null;
  }

  return (
    <AccessGroupForm
      mode="edit"
      id={accessGroupId}
      defaultValues={accessGroup}
      onClickCancel={handleCancel}
      onSuccess={handleSuccess}
    />
  );
};
